<template>
  <stihl-modal id="tutorials-modal" @modal-closed="$emit('modalClosed')">

    <h3 class="title" v-text="$t('adm.page.tutorials.title')" />

    <h4 class="subtitle">
      <a
        :href="resolveAssetURL($t('adm.page.tutorials.docSRC'))"
        class="link-with-icon"
        target="_blank"
        rel="noopener noreferrer">
        <img src="@/assets/img/icons/arrow-down-line.svg" alt="download">
        {{ $t('adm.page.tutorials.download') }}
      </a>
    </h4>

    <div class="row row-cols-2">

      <div class="col" v-for="(tutorial, i) in tutorials" :key="i">
        <tutorial-card
          :subtitle="tutorial.subtitle"
          :text="tutorial.text"
          :poster="tutorial.poster"
          :sources="tutorial.sources"
          :tracks="tutorial.tracks" />
      </div>

    </div>

    <div class="bottom-bar">
      <div class="form-check float-end">
        <input id="cHide" class="form-check-input" type="checkbox" v-model="hideOnStartup" @click="toggleHideOnStartup">
        <label for="cHide" class="form-check-label">{{ $t('adm.page.tutorials.hide') }}</label>
      </div>
    </div>

  </stihl-modal>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import StihlModal from '@/components/ui/StihlModal.vue';
import TutorialCard, { TrackSource, VideoSource } from '@/components/ui/TutorialCard.vue';
import useAppStore from '@/components/store';
import { mapWritableState } from 'pinia';
import Backend from '@/components/backend';

export default defineComponent({
  name: 'VideoTutorials',
  components: { TutorialCard, StihlModal },
  emits: ['modalClosed'],
  computed: {
    // inject state getters
    ...mapWritableState(useAppStore, {
      hideOnStartup: 'hideTutorialsOnStartup',
    }),

    tutorials() {
      return ['createUser', 'checkUserSettings', 'forgotPassword', 'deleteUser'].map((key) => ({
        subtitle: this.$t(`adm.tutorials.${key}.subtitle`),
        text: this.$t(`adm.tutorials.${key}.text`),
        poster: this.$t(`adm.tutorials.${key}.poster`),
        sources: [{
          type: 'video/mp4',
          src: this.$t(`adm.tutorials.${key}.video`),
        } as VideoSource],
        tracks: this.$te(`adm.tutorials.${key}.track.src`, 'en') ? [{
          type: 'subtitles',
          src: this.$t(`adm.tutorials.${key}.track.src`),
          lang: this.$t(`adm.tutorials.${key}.track.lang`),
          label: this.$t(`adm.tutorials.${key}.track.label`),
          default: true,
        } as TrackSource] : [],
      }));
    },
  },
  methods: {
    toggleHideOnStartup() {
      Backend.validateToken().then((api) => {
        const newValue = !this.hideOnStartup;
        api.saveMyPreferences({ hideTutorials: newValue })
          .then(() => {
            this.hideOnStartup = newValue;
          })
          .catch(() => this.$toast.error(this.$t('adm.error.generic')));
      });
    },
    resolveAssetURL: (ressource: string) => (`${import.meta.env.BASE_URL}assets/doc/${ressource}`),
  },
});
</script>

<style>
#tutorials-modal {
  --stihl-modal-content-gap: 2.5rem;
  --bs-modal-width: 83rem;
}

#tutorials-modal .title {
  flex: 1 0 0;
  display: flex;
  align-content: center;
  gap: .5rem;
  color: black;
  font-feature-settings: 'clig' off, 'liga' off;
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.5rem;
  margin: 0;
}

#tutorials-modal .subtitle {
  color: var(--grey-deep);
  font-feature-settings: 'clig' off, 'liga' off;
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 700;
  line-height: 1.5rem;
}

#tutorials-modal .card {
  --bs-border-width: 0;
}

#tutorials-modal .bottom-bar {
  margin: -2rem 0;
}

</style>
